"use client";

import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useState } from "react";

const STAGES = [
  {
    score: 34,
    strokeClass: "text-red-600 dark:text-red-500",
    shadowClass: "drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]",
    textClass: "text-red-700 dark:text-red-400",
    blurClass: "bg-red-500/20 dark:bg-red-500/10",
    pulseRing: "border-red-500/30 dark:border-red-500/20",
    labelClass: "text-red-700/80 dark:text-red-500/80",
  },
  {
    score: 72,
    strokeClass: "text-amber-600 dark:text-amber-500",
    shadowClass: "drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]",
    textClass: "text-amber-700 dark:text-amber-400",
    blurClass: "bg-amber-500/20 dark:bg-amber-500/10",
    pulseRing: "border-amber-500/30 dark:border-amber-500/20",
    labelClass: "text-amber-700/80 dark:text-amber-500/80",
  },
  {
    score: 92,
    strokeClass: "text-emerald-600 dark:text-emerald-500",
    shadowClass: "drop-shadow-[0_0_8px_rgba(16,185,129,0.7)]",
    textClass: "text-emerald-700 dark:text-emerald-400",
    blurClass: "bg-emerald-500/20 dark:bg-emerald-500/10",
    pulseRing: "border-emerald-500/30 dark:border-emerald-500/20",
    labelClass: "text-emerald-700/80 dark:text-emerald-500/80",
  },
  {
    score: 100,
    strokeClass: "text-emerald-700 dark:text-emerald-400",
    shadowClass: "drop-shadow-[0_0_12px_rgba(16,185,129,0.9)]",
    textClass: "text-emerald-800 dark:text-emerald-300",
    blurClass: "bg-emerald-400/30 dark:bg-emerald-400/20",
    pulseRing: "border-emerald-500/40 dark:border-emerald-400/30",
    labelClass: "text-emerald-700 dark:text-emerald-400",
  },
];

export default function Preview() {
  const [step, setStep] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Framer Motion Zähler für das weiche Hochzählen der Zahl
  const count = useMotionValue(STAGES[0].score);
  const rounded = useTransform(count, Math.round);

  // Intervall-Steuerung
  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setStep((prev) => (prev + 1) % STAGES.length);
    }, 2800); // Minimal länger, damit die 100 am Ende kurz wirken kann
    return () => clearInterval(interval);
  }, []);

  const current = STAGES[step];

  const circumference = 289;
  const strokeDashoffset =
    circumference - (circumference * current.score) / 100;

  // Animiert die Zahl genau im selben Rhythmus wie den SVG-Kreis
  useEffect(() => {
    if (!mounted) return;
    const animation = animate(count, current.score, {
      duration: 1, // Exakt die gleiche Dauer wie der SVG Kreis unten
      type: "tween",
      ease: "easeInOut",
    });
    return animation.stop;
  }, [current.score, count, mounted]);

  if (!mounted) return <div className="min-h-37.5 h-full w-full" />;

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl p-4">
      {/* Animated tech grid background */}
      <motion.div
        className="absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.10)_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)]"
        animate={{ backgroundPosition: ["0px 0px", "14px 24px"] }}
        transition={{
          repeat: Infinity,
          repeatType: "loop",
          duration: 3,
          ease: "linear",
        }}
      />

      <motion.div
        className={`absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-colors duration-1000 sm:h-56 sm:w-56 ${current.blurClass}`}
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
      />

      <div className="relative z-10 flex flex-col items-center">
        <div className="relative flex aspect-square w-24 items-center justify-center rounded-full sm:w-28">
          <svg
            className="absolute inset-0 h-full w-full -rotate-90 overflow-visible"
            viewBox="0 0 100 100"
          >
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              strokeWidth="6"
              className="stroke-slate-100 dark:stroke-slate-800"
            />
            <motion.circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              className={`transition-colors duration-1000 ${current.strokeClass} ${current.shadowClass}`}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1, type: "tween", ease: "easeInOut" }}
            />
          </svg>

          {/* Anstatt {current.score} verwenden wir hier {rounded} für weiches Hochzählen */}
          <motion.span
            className={`text-3xl font-bold tabular-nums leading-none transition-colors duration-1000 sm:text-4xl ${current.textClass} ${current.shadowClass}`}
          >
            {rounded}
          </motion.span>
        </div>
      </div>
    </div>
  );
}
